// Pure, side-effect-free cost-view logic (spec 690). No React or network here so
// it stays unit-testable (cost.test.ts). Data shapes come from the spec-689 backend.

import type { CostSummary, CostByOrg, OrgCostRow, Unpriced } from "./api";

export type PresetKey = "this_month" | "last_month" | "last_30d";

export type Period =
  | { kind: "preset"; preset: PresetKey }
  | { kind: "custom"; start: string; end: string };

/** The three preset keys the backend `_resolve_period` accepts, with UI labels. */
export const PRESETS: { key: PresetKey; label: string }[] = [
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "last_30d", label: "Last 30 days" },
];

/** Query params for the cost endpoints from a period selection. */
export function periodParams(p: Period): Record<string, string> {
  return p.kind === "preset" ? { preset: p.preset } : { start: p.start, end: p.end };
}

/**
 * Client-side guard that mirrors the backend 400s (invalid / `end <= start`).
 * The backend treats `end` as an inclusive date, so a single-day range
 * (end === start) is valid. Returns an error message, or null when valid.
 */
export function validateCustomRange(start: string, end: string): string | null {
  if (!start || !end) return "Choose both a start and end date.";
  if (end < start) return "End date must be on or after the start date.";
  return null;
}

/** USD formatting. null/undefined → "n/a" (never a guessed $0); 0 is a real value. */
export function money(n: number | null | undefined): string {
  if (n === null || n === undefined) return "n/a";
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

/** Compact integer formatting for counts/tokens. */
export function count(n: number): string {
  return n.toLocaleString("en-US");
}

/**
 * The distinct offending labels behind unpriced usage — the model_key when the
 * category is model-scoped (llm_*), else the category itself (telephony/stt have
 * no model). Lets the trust banner name WHICH model drifted off the rate card
 * (#806), so a deploy that rolls to an unpriced model is obvious, not buried.
 * Insertion order preserved (deterministic).
 */
export function unpricedModels(unpriced: Unpriced[]): string[] {
  const labels: string[] = [];
  for (const u of unpriced) {
    const label = u.model_key || u.category;
    if (!labels.includes(label)) labels.push(label);
  }
  return labels;
}

// --- CSV export (client-side; aggregated metadata only, FR-012) --------------

/** RFC-4180 cell escaping: quote when the value has a comma, quote, or newline. */
function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** Numeric money for a spreadsheet: 2-decimals, or "n/a" when unknown. */
function csvMoney(n: number | null | undefined): string {
  return n === null || n === undefined ? "n/a" : n.toFixed(2);
}

function orgLabel(o: OrgCostRow): string {
  return o.org_name || o.org_id;
}

/**
 * Build the CSV export: report banner, company totals, per-org table, and the
 * unpriced-usage list — the same numbers shown on screen, aggregated only.
 */
export function buildCostCsv(summary: CostSummary, byOrg: CostByOrg): string {
  const lines: string[] = [];
  const row = (...cells: (string | number)[]) => lines.push(cells.map(csvCell).join(","));

  row("Wingscript cost report (modeled estimate)");
  row("Period", summary.period_start, summary.period_end);
  lines.push("");

  row("Company totals");
  row("Total variable cost", csvMoney(summary.total_variable_usd));
  row("LLM", csvMoney(summary.llm_usd));
  row("Telephony", csvMoney(summary.telephony_usd));
  row("STT (approximate)", csvMoney(summary.stt_usd));
  row("Fixed infra", summary.fixed_infra.status === "not_integrated" ? "not integrated" : csvMoney(summary.fixed_infra.usd));
  row("Calls", summary.calls);
  row("Active orgs", summary.active_orgs);
  row("Cost per call", csvMoney(summary.cost_per_call));
  row("Cost per telephony-minute", csvMoney(summary.cost_per_minute));
  row("Cost per active org", csvMoney(summary.cost_per_active_org));
  lines.push("");

  row("Per-org");
  row("Org", "Tier", "LLM", "Telephony", "STT", "Total", "Revenue", "Margin", "Calls");
  for (const o of byOrg.orgs) {
    row(
      orgLabel(o),
      o.plan_tier || "—",
      csvMoney(o.llm_usd),
      csvMoney(o.telephony_usd),
      csvMoney(o.stt_usd),
      csvMoney(o.total_variable_usd),
      o.revenue_known ? csvMoney(o.revenue_usd) : "n/a",
      o.revenue_known ? csvMoney(o.margin_usd) : "n/a",
      o.calls,
    );
  }

  const unpriced = summary.unpriced.length ? summary.unpriced : byOrg.unpriced;
  if (unpriced.length) {
    lines.push("");
    row("Unpriced usage (not valued)");
    row("Category", "Model", "Tokens", "Minutes");
    for (const u of unpriced) {
      row(u.category, u.model_key || "—", u.tokens ?? "", u.minutes ?? "");
    }
  }

  return lines.join("\n");
}
